/**
 * `GET /v1/me/net-worth` (TMMAPP-0018, design §12.5) — the five totals, the five item lists,
 * `valuedAsOf`, and the 12-month `series[]`. Wire types from `src/api/generated/`; every figure
 * is a served field (MM-D-23).
 */
import { request } from './client';
import type {
  NetWorthItem as WireItem,
  NetWorthResponse as WireNetWorth,
  NetWorthSeriesPoint as WirePoint,
} from './generated';

export interface NetWorthItem {
  id: number;
  name: string;
  valueMinor: number;
}

export interface NetWorthSeriesPoint {
  asOf: string;
  netWorthMinor: number;
}

export interface NetWorth {
  /** Always served; null only for the guest view, which has no net worth to date. */
  asOf: string | null;
  valuedAsOf: string | null;
  cashMinor: number;
  revolvingMinor: number;
  loansMinor: number;
  fixedAssetsMinor: number;
  tradableMinor: number;
  netWorthMinor: number;
  cash: NetWorthItem[];
  revolving: NetWorthItem[];
  loans: NetWorthItem[];
  fixedAssets: NetWorthItem[];
  tradable: NetWorthItem[];
  series: NetWorthSeriesPoint[];
  /**
   * True when at least one unmanaged account is included in the totals (TMMAPP-0026): the wire's
   * `unmanagedIncluded` lists them.
   */
  unmanagedIncluded: boolean;
  /** Accounts counted from 0 because their opening is pending (TMMAPI-0160 U2). */
  openingPendingAccountIds: number[];
}

function normalizeItem(w: WireItem): NetWorthItem {
  return { id: w.id, name: w.name, valueMinor: w.valueMinor };
}

function normalizePoint(w: WirePoint): NetWorthSeriesPoint {
  return { asOf: w.asOf, netWorthMinor: w.netWorthMinor };
}

export function normalizeNetWorth(w: WireNetWorth): NetWorth {
  const series = w.series
    .map(normalizePoint)
    .sort((a, b) => (a.asOf < b.asOf ? -1 : a.asOf > b.asOf ? 1 : 0));
  return {
    asOf: w.asOf,
    valuedAsOf: w.valuedAsOf ?? null,
    cashMinor: w.cashMinor,
    revolvingMinor: w.revolvingMinor,
    loansMinor: w.loansMinor,
    fixedAssetsMinor: w.fixedAssetsMinor,
    tradableMinor: w.tradableMinor,
    netWorthMinor: w.netWorthMinor,
    cash: w.cash.map(normalizeItem),
    revolving: w.revolving.map(normalizeItem),
    loans: w.loans.map(normalizeItem),
    fixedAssets: w.fixedAssets.map(normalizeItem),
    tradable: w.tradable.map(normalizeItem),
    series,
    unmanagedIncluded: (w.unmanagedIncluded ?? []).length > 0,
    openingPendingAccountIds: w.openingPendingAccountIds,
  };
}

export const netWorthApi = {
  /** GET /v1/me/net-worth?asOf= — omit `asOf` for today. */
  async get(asOf?: string): Promise<NetWorth> {
    return normalizeNetWorth(
      await request<WireNetWorth>('GET', '/v1/me/net-worth', {
        query: asOf !== undefined ? { asOf } : {},
      }),
    );
  },
};
